import { supabase } from '../supabaseClient';
import { apiClient } from './api';
import { API_BASE_URL } from './api';
import { RepresentativeInfo, LawyerFormData } from '../types/lawyer';

export class LawyerInfoService {
  
  // Get lawyer representative information
  // Uses backend API which uses service role key (bypasses RLS)
  async getLawyerInfo(lawyerId: string) {
    try {
      const response = await apiClient.getUserProfile();
      
      if (response.data) {
        // Log industry when fetching lawyer info for debugging
        console.log(`📋 Fetched lawyer info. Industry: ${response.data.industry || 'NOT SET'}`);
        return { data: response.data, error: null };
      } else {
        // User doesn't exist - return null (not an error)
        return { data: null, error: null };
      }
    } catch (error: any) {
      console.error('Error fetching lawyer info:', error);
      // If 404 or user doesn't exist, return null data
      if (error.response?.status === 404 || error.message?.includes('not found')) {
        return { data: null, error: null };
      }
      return { data: null, error };
    }
  }

  // Save lawyer representative information
  // Uses backend API which bypasses RLS (uses service role key)
  async saveLawyerInfo(lawyerId: string, formData: LawyerFormData) {
    try {
      // Create representative info JSON structure
      const representativeInfo: RepresentativeInfo = {
        lastName: formData.lastName,
        firstName: formData.firstName,
        lawFirm: formData.lawFirm,
        supervisingLawyerName: formData.supervisingLawyerName || '',
        supervisingLawyerMembershipID: formData.supervisingLawyerMembershipID || '',
        address: {
          streetNumber: formData.address.streetNumber,
          streetName: formData.address.streetName,
          aptNumber: formData.address.aptNumber || '',
          city: formData.address.city,
          province: formData.address.province,
          country: formData.address.country,
          postalCode: formData.address.postalCode
        },
        phoneCountryCode: formData.phoneCountryCode,
        phoneNumber: formData.phoneNumber,
        faxCountryCode: formData.faxCountryCode || '',
        faxNumber: formData.faxNumber || '',
        email: formData.email
      };

      // Use backend API - bypasses RLS by using service role key
      // Ensure industry is always sent, default to 'legal' if not set
      const industryValue = formData.industry || 'legal';
      console.log('💾 Saving lawyer profile with industry:', industryValue);
      
      const response = await apiClient.updateUserProfile({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phoneNumber,
        phoneNumber: formData.phoneNumber,
        lawFirm: formData.lawFirm,
        supervisingLawyerName: formData.supervisingLawyerName,
        supervisingLawyerMembershipID: formData.supervisingLawyerMembershipID,
        address: formData.address,
        phoneCountryCode: formData.phoneCountryCode,
        faxCountryCode: formData.faxCountryCode,
        faxNumber: formData.faxNumber,
        representativeInfo: representativeInfo,
        industry: industryValue // Always send industry, default to 'legal'
      });

      // Backend returns { success: true, data: ... } on success
      if (response.success) {
        return { error: null };
      } else {
        return { 
          error: response.error || { message: response.error || 'Failed to save profile' }
        };
      }
    } catch (error: any) {
      console.error('Error saving lawyer info:', error);
      // Handle API errors (401, 500, etc.)
      const errorMessage = error.response?.data?.error || error.message || 'Failed to save profile';
      return { 
        error: { 
          message: errorMessage,
          code: error.response?.status || 'UNKNOWN_ERROR'
        }
      };
    }
  }

  async uploadProfileImage(file: File) {
    return apiClient.uploadProfileImage(file);
  }

  async uploadFirmLogo(file: File) {
    return apiClient.uploadFirmLogo(file);
  }

  async deleteProfileImage() {
    return apiClient.deleteProfileImage();
  }

  async deleteFirmLogo() {
    return apiClient.deleteFirmLogo();
  }

  // Check if lawyer profile is complete
  // Uses backend API
  async isProfileComplete(lawyerId: string) {
    try {
      const response = await apiClient.checkProfileComplete();
      return response.isComplete || false;
    } catch (error) {
      console.error('Error in isProfileComplete:', error);
      return false;
    }
  }

  // Get lawyer info for form generation
  // Uses backend API which uses service role key (bypasses RLS)
  // This is a public endpoint - no authentication required (for public forms)
  async getLawyerInfoForForms(lawyerId: string) {
    try {
      // Use direct fetch for public endpoint (no auth required)
      const response = await fetch(`${API_BASE_URL}/users/${lawyerId}/representative-info`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          // User not found - return null (not an error)
          return { data: null, error: null };
        }
        const errorData = await response.json().catch(() => ({}));
        console.error('Error fetching lawyer info for forms:', errorData);
        return { data: null, error: errorData };
      }

      const result = await response.json();
      
      if (result.data) {
        return { data: result.data, error: null };
      } else {
        // No representative info found - return null (not an error)
        return { data: null, error: null };
      }
    } catch (error: any) {
      console.error('Error in getLawyerInfoForForms:', error);
      return { data: null, error };
    }
  }
}

export const lawyerInfoService = new LawyerInfoService(); 